import classNames from "classnames";
import * as React from "react";

import DocStyledText from "../BlockNodeHelpers/StyledText";
import PlaceholderImage from "images/placeholder-image.png";
import { MEDIA_PICKER } from "../../../helpers/Doc/menu";
import {
  backgroundStyleFromNode,
  newAddNodeChange,
  newDeleteNodeChange,
} from "../../../helpers/Doc/changes";
import { isRangeInlineJSON } from "../../../helpers/Doc/Functions/RangeInlineJSON";

const DAM_DISTRIBUTION_ENDPOINT = "https://d3pk024wjiydi7.cloudfront.net/filestore/";

interface PatientInformationField {
  label: string;
  contentKey: keyof DocPatientInformationType<InlineNode>;
  placeholder: string;
}

const LEFT_COLUMN_FIELDS: PatientInformationField[] = [
  { label: "Age", contentKey: "age", placeholder: "Patient Age" },
  {
    label: "Language for medical communication",
    contentKey: "languageForMedicalCommunication",
    placeholder: "Language",
  },
];

const RIGHT_COLUMN_FIELDS: PatientInformationField[] = [
  { label: "Sex assigned at birth", contentKey: "sexAssignedAtBirth", placeholder: "Sex" },
  { label: "Gender identity", contentKey: "genderIdentity", placeholder: "Gender" },
  { label: "Pronouns", contentKey: "pronouns", placeholder: "Pronoun" },
];

interface PatientInformationProps
  extends DocPatientInformationType<InlineNode>,
    BlockNodeAll {}

interface PatientInformationSectionContext {
  sectionUid: string;
  sectionContentLength: number;
  showRemoveButton: boolean;
  showAddMoreButton: boolean;
}

function readPatientInformationSectionContext(
  nodeElement: HTMLElement | null,
  nodeUid: string,
  isEditing: boolean,
  displayContext: DisplayContext
): PatientInformationSectionContext | null {
  if (!nodeElement) {
    return null;
  }

  const sectionElement = nodeElement.closest(".doc-section");
  if (!sectionElement?.classList.contains("patient_information_card")) {
    return null;
  }

  const childrenContainer = sectionElement.querySelector(
    ".doc-section-body > .doc-children"
  );
  if (!childrenContainer) {
    return null;
  }

  const patientInformationElements = Array.from(
    childrenContainer.querySelectorAll(":scope > .patient-information[data-is-node]")
  ).filter((element) => element.getAttribute("data-is-deleted") !== "true");
  const patientInformationCount = patientInformationElements.length;
  const isLastPatientInformation =
    patientInformationElements[patientInformationCount - 1]?.id === nodeUid;
  const sectionCollapsed = sectionElement.classList.contains("collapsed");

  return {
    sectionUid: sectionElement.getAttribute("data-uid") ?? "",
    sectionContentLength: patientInformationCount,
    showRemoveButton: isEditing && patientInformationCount > 1,
    showAddMoreButton:
      isEditing &&
      (displayContext.isDisplayFull || !sectionCollapsed) &&
      isLastPatientInformation,
  };
}

function isInlineContentEmpty(content: InlineNode | undefined): boolean {
  if (content === undefined || content === null || content === "") {
    return true;
  }
  if (typeof content === "string") {
    return content.replace(/<[^>]+>/g, "").trim().length === 0;
  }
  if (isRangeInlineJSON(content)) {
    return !content.text?.trim();
  }
  if (Array.isArray(content)) {
    return (content as InlineNode[]).length === 0;
  }
  return false;
}

function inlineContentToPlainText(content: InlineNode | undefined): string {
  if (isInlineContentEmpty(content)) {
    return "";
  }
  if (typeof content === "string") {
    return content.replace(/<[^>]+>/g, "").trim();
  }
  if (isRangeInlineJSON(content)) {
    return content.text?.trim() ?? "";
  }
  return "";
}

interface PatientInformationStyledFieldProps {
  uid: string;
  contentKey: keyof DocPatientInformationType<InlineNode>;
  content: InlineNode;
  placeholder: string;
  editingContext: EditingContext;
  isEditing: boolean;
  readOnly?: boolean;
  className?: string;
}

function PatientInformationStyledField({
  uid,
  contentKey,
  content,
  placeholder,
  editingContext,
  isEditing,
  readOnly,
  className,
}: PatientInformationStyledFieldProps) {
  if (!isEditing) {
    if (isInlineContentEmpty(content)) {
      return (
        <span className={classNames("patient-information__value", className)}>
          {placeholder}
        </span>
      );
    }

    return (
      <span className={classNames("patient-information__value", className)}>
        <DocStyledText
          content={content}
          editingContext={editingContext}
          readOnly
          noSpan
        />
      </span>
    );
  }

  return (
    <span
      className={classNames(
        { "patient-information__field-input": !className },
        className
      )}
    >
      <DocStyledText
        uid={uid}
        content={content ?? ""}
        contentKey={contentKey}
        placeholder={placeholder}
        editingContext={editingContext}
        independentlyEditable
        readOnly={readOnly}
      />
    </span>
  );
}

interface FieldRowProps {
  field: PatientInformationField;
  uid: string;
  content: InlineNode;
  editingContext: EditingContext;
  isEditing: boolean;
  readOnly?: boolean;
}

function FieldRow({ field, uid, content, editingContext, isEditing, readOnly }: FieldRowProps) {
  return (
    <li className="patient-information__detail">
      <span className="patient-information__label">{field.label}:</span>{" "}
      <PatientInformationStyledField
        uid={uid}
        contentKey={field.contentKey}
        content={content}
        placeholder={field.placeholder}
        editingContext={editingContext}
        isEditing={isEditing}
        readOnly={readOnly}
      />
    </li>
  );
}

function PatientInformation(props: PatientInformationProps) {
  const { editingContext, mediaContext, currentVersion, blockChangeHighlight, displayContext, uid } =
    props;
  const isEditing = editingContext.isEditing;
  const isDelete = blockChangeHighlight?.type === "DELETE_CONTENT";
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [sectionContext, setSectionContext] =
    React.useState<PatientInformationSectionContext | null>(null);

  React.useLayoutEffect(() => {
    const nodeElement = rootRef.current;
    if (!nodeElement) {
      return;
    }

    const sectionElement = nodeElement.closest(".doc-section");
    if (!sectionElement?.classList.contains("patient_information_card")) {
      return;
    }

    const childrenContainer = sectionElement.querySelector(
      ".doc-section-body > .doc-children"
    );
    if (!childrenContainer) {
      return;
    }

    const updateSectionContext = () => {
      setSectionContext(
        readPatientInformationSectionContext(nodeElement, uid, isEditing, displayContext)
      );
    };

    updateSectionContext();

    const observer = new MutationObserver(updateSectionContext);
    observer.observe(childrenContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-is-deleted"],
    });

    return () => observer.disconnect();
  }, [uid, isEditing, displayContext]);

  const showRemoveButton = (sectionContext?.showRemoveButton ?? false) && !isDelete;
  const showAddMoreButton = sectionContext?.showAddMoreButton ?? false;
  const sectionUid = sectionContext?.sectionUid;
  const sectionContentLength = sectionContext?.sectionContentLength;

  const getImageURL = () => {
    const { url, uri } = props;
    if (url) {
      const asset = mediaContext.images.find(
        (i) =>
          i.distribution_url === url ||
          url.includes(i.asset_path) ||
          url.includes(i.asset_path.replace("/filestore/", ""))
      );
      const hasDomain = url.startsWith("http://") || url.startsWith("https://");
      return (
        (hasDomain ? url : asset?.distribution_url) ||
        `${DAM_DISTRIBUTION_ENDPOINT}${url}`
      );
    }
    if (uri && uri !== "blank.jpg") {
      return currentVersion.localImageURL(uri);
    }
    return PlaceholderImage;
  };

  const imageURL = getImageURL();
  const altText = inlineContentToPlainText(props.patientName) || "Patient photo";

  const canPickImage =
    isEditing && props.userContext.permissions.viewMediaAssets;

  const openPicker = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canPickImage) {
      editingContext.setCurrentMenu(MEDIA_PICKER, {
        mediaType: "image",
        uid: props.uid,
      });
    }
  };

  const onImageKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker(e);
    }
  };

  const renderFieldList = (fields: PatientInformationField[]) => (
    <ul className="patient-information__details">
      {fields.map((field) => (
        <FieldRow
          key={field.contentKey}
          field={field}
          uid={props.uid}
          content={(props[field.contentKey] as InlineNode) ?? ""}
          editingContext={editingContext}
          isEditing={isEditing}
          readOnly={isDelete}
        />
      ))}
    </ul>
  );

  const editingContextInEditor = editingContext as EditingContextInEditor;

  const removePatientInformation = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!showRemoveButton) {
      return;
    }
    editingContextInEditor.clearCurrentDocSelection();
    editingContextInEditor.addChange(newDeleteNodeChange(props.uid));
  };

  const addPatientInformation = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!sectionUid || sectionContentLength === undefined) {
      return;
    }
    editingContextInEditor.clearCurrentDocSelection();
    const newNode = editingContextInEditor.nodeDefinitions.patientInformation.generate(
      editingContextInEditor.nodeDefinitions,
      {}
    );
    editingContextInEditor.addChange(
      newAddNodeChange(newNode, sectionUid, "content", sectionContentLength)
    );
  };

  return (
    <>
    <div
      ref={rootRef}
      id={props.uid}
      data-is-node
      data-uid={props.uid}
      data-is-deleted={isDelete ? "true" : undefined}
      className={classNames("patient-information", props.uid, props.classes)}
      contentEditable={false}
      style={backgroundStyleFromNode(props)}
    >
      {isEditing && showRemoveButton && (
        <button
          type="button"
          className="patient-information__remove-button no-document-selection"
          onClick={removePatientInformation}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Remove patient information"
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      )}
      <div className="patient-information__layout">
        <div className="patient-information__image-column">
          {canPickImage ? (
            <button
              type="button"
              className={classNames(
                "patient-information__image",
                "patient-information__image--editable",
                "no-document-selection"
              )}
              onClick={openPicker}
              onKeyDown={onImageKeyDown}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Choose patient photo"
            >
              <img src={imageURL} alt={altText} />
              <span className="patient-information__image-edit-icon" aria-hidden="true">
                <i className="fa fa-edit" />
              </span>
            </button>
          ) : (
            <div className="patient-information__image">
              <img src={imageURL} alt={altText} />
            </div>
          )}
        </div>
        <div className="patient-information__content">
          {isEditing ? (
            <div className="patient-information__name-field">
              <PatientInformationStyledField
                uid={props.uid}
                contentKey="patientName"
                content={props.patientName ?? ""}
                placeholder="Patient Name"
                editingContext={editingContext}
                isEditing={isEditing}
                readOnly={isDelete}
              />
            </div>
          ) : (
            <h2 className="patient-information__name">
              {isInlineContentEmpty(props.patientName) ? (
                "Patient Name"
              ) : (
                <DocStyledText
                  content={props.patientName}
                  editingContext={editingContext}
                  readOnly
                  noSpan
                />
              )}
            </h2>
          )}
          <div className="patient-information__columns">
            {renderFieldList(LEFT_COLUMN_FIELDS)}
            {renderFieldList(RIGHT_COLUMN_FIELDS)}
          </div>
        </div>
      </div>
    </div>
    {showAddMoreButton && (
      <div className="patient-information-add-more" contentEditable={false}>
        <button
          type="button"
          className="patient-information-add-more-button no-document-selection"
          onClick={addPatientInformation}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <i className="fa fa-plus" aria-hidden="true" />
          Add more patient information
        </button>
      </div>
    )}
    </>
  );
}

export default React.memo(PatientInformation);